import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { plainText } from './propertyUtils';

const LOGO_DEFAULT =
  '';

const contactDef: SlotGroupDefinition = {
  label: 'Contact CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/contact-us',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Say Hello!',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

const socialDef: SlotGroupDefinition = {
  label: 'Social Links',
  fields: {
    instagram: {
      label: 'Instagram URL',
      type: 'url',
      defaultValue: 'https://www.instagram.com/americanlandmark/',
    },
    linkedin: {
      label: 'LinkedIn URL',
      type: 'url',
      defaultValue: 'https://www.linkedin.com/company/american-landmark-apartments/',
    },
    facebook: {
      label: 'Facebook URL',
      type: 'url',
      defaultValue: 'https://www.facebook.com/AmericanLandmarkApartments/',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

const INFO_LINKS = [
  { href: '/privacy-policy', label: 'Privacy Policy' },
  { href: '/accessibility-statement', label: 'Accessibility Statement' },
];

const LEARN_LINKS = [
  { href: '/about-us', label: 'About Us' },
  { href: '/careers', label: 'Careers' },
  { href: '/faqs', label: 'FAQs' },
];

const ACCOUNT_LINKS = [
  { href: '/resident-portal', label: 'Resident Portal', external: false },
  { href: 'https://www.alapts.com/investors', label: 'Investor Login', external: true },
  { href: '/contact-us', label: 'Contact Us', external: false },
];

function SocialIcon({ name }: { name: 'instagram' | 'linkedin' | 'facebook' }) {
  if (name === 'instagram') {
    return (
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden>
        <path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5zm5 5a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm6.5-.9a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2zM12 9a3 3 0 1 1 0 6 3 3 0 0 1 0-6z" />
      </svg>
    );
  }
  if (name === 'linkedin') {
    return (
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden>
        <path d="M4.98 3.5C4.98 4.88 3.86 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.5 8.5h4V23h-4V8.5zM8.5 8.5h3.8v2h.05c.53-1 1.84-2.05 3.79-2.05 4.05 0 4.8 2.67 4.8 6.14V23h-4v-6.6c0-1.57-.03-3.59-2.19-3.59-2.19 0-2.53 1.71-2.53 3.48V23h-3.92V8.5z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden>
      <path d="M22.7 0H1.3C.6 0 0 .6 0 1.3v21.4C0 23.4.6 24 1.3 24h11.5v-9.3H9.7V11h3.1V8.4c0-3.1 1.9-4.8 4.7-4.8 1.3 0 2.5.1 2.8.1v3.3h-1.9c-1.5 0-1.8.7-1.8 1.8V11h3.6l-.5 3.7h-3.1V24h6.1c.7 0 1.3-.6 1.3-1.3V1.3C24 .6 23.4 0 22.7 0z" />
    </svg>
  );
}

function SocialCircle({
  href,
  target,
  label,
  name,
}: {
  href: string;
  target: string;
  label: string;
  name: 'instagram' | 'linkedin' | 'facebook';
}) {
  return (
    <a
      href={href}
      target={target}
      rel="noopener noreferrer"
      aria-label={label}
      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[rgb(var(--ala-accent))] text-[rgb(var(--ala-on-primary))] transition hover:bg-[rgb(var(--ala-accent))]"
    >
      <SocialIcon name={name} />
    </a>
  );
}

export function Footer() {
  const logo = useContentToken('footer.company_logo');
  const logoAlt = plainText(useContentToken('footer.company_logo_alt'));
  const contact = useSlotGroup('footer.contact_link', contactDef);
  const social = useSlotGroup('footer.social', socialDef);
  const socialTarget = social._target || '_blank';

  return (
    <footer
      className="flex w-full justify-center px-4 pb-8 pt-20 text-[rgb(var(--ala-on-primary))] md:px-8 md:pt-28"
      style={{ backgroundColor: 'rgb(var(--ala-dark))' }}
    >
      <div className="flex w-full max-w-[1440px] flex-col">
        {/* Top: CTA stack left, logo right */}
        <div className="flex flex-col items-start justify-between gap-12 pb-16 lg:flex-row lg:items-center">
          <div className="flex max-w-[470px] flex-col gap-8">
            <RichTextContent
              path="footer.headline"
              label="Headline"
              as="h2"
              className="font-heading text-[clamp(32px,2.59vw,42px)] font-medium leading-[1.1] tracking-[-0.04em]"
            />
            <EditableSlotGroup groupPath="footer.contact_link" definition={contactDef}>
              <a
                href={contact.url || '/contact-us'}
                target={contact._target || '_self'}
                className="inline-flex w-fit items-center gap-2 rounded-full px-6 py-3.5 font-heading text-sm uppercase tracking-wide text-[rgb(var(--ala-on-secondary))] transition hover:opacity-90"
                style={{
                  background:
                    'linear-gradient(90deg, rgb(var(--ala-secondary)), color-mix(in srgb, rgb(var(--ala-secondary)) 70%, #1a4a9e))',
                }}
              >
                {contact.label || 'Say Hello!'}
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                  <path d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </a>
            </EditableSlotGroup>

            <EditableSlotGroup groupPath="footer.social" definition={socialDef}>
              <div className="flex items-center gap-3">
                {social.instagram ? (
                  <SocialCircle href={social.instagram} target={socialTarget} label="Instagram" name="instagram" />
                ) : null}
                {social.linkedin ? (
                  <SocialCircle href={social.linkedin} target={socialTarget} label="LinkedIn" name="linkedin" />
                ) : null}
                {social.facebook ? (
                  <SocialCircle href={social.facebook} target={socialTarget} label="Facebook" name="facebook" />
                ) : null}
              </div>
            </EditableSlotGroup>
          </div>

          <a href="/" className="inline-block self-start lg:self-center">
            <EditableToken path="footer.company_logo" type="image" as="div" label="Footer Logo">
              <VibesitesImage
                url={logo || LOGO_DEFAULT}
                alt={logoAlt || 'American Landmark'}
                width={320}
                height={80}
                objectFit="contain"
                sizes="320px"
                className="h-auto w-[min(100%,280px)] brightness-0 invert md:w-[320px]"
              />
            </EditableToken>
          </a>
        </div>

        {/* Middle: INFO left, LEARN MORE + ACCOUNT right */}
        <div className="flex flex-col gap-10 border-t border-white/10 py-12 md:flex-row md:justify-between">
          <div>
            <p className="mb-4 font-heading text-xs uppercase tracking-[0.2em] text-[rgb(var(--ala-on-primary))]/50">
              Info
            </p>
            <nav className="flex flex-col gap-3 font-body text-sm text-[rgb(var(--ala-on-primary))]/85">
              {INFO_LINKS.map((link) => (
                <a key={link.href} href={link.href} className="transition hover:text-[rgb(var(--ala-accent))]">
                  {link.label}
                </a>
              ))}
            </nav>
          </div>

          <div className="flex flex-col gap-10 sm:flex-row sm:gap-16 lg:gap-24">
            <div>
              <p className="mb-4 font-heading text-xs uppercase tracking-[0.2em] text-[rgb(var(--ala-on-primary))]/50">
                Learn More
              </p>
              <nav className="flex flex-col gap-3 font-body text-sm text-[rgb(var(--ala-on-primary))]/85">
                {LEARN_LINKS.map((link) => (
                  <a key={link.href} href={link.href} className="transition hover:text-[rgb(var(--ala-accent))]">
                    {link.label}
                  </a>
                ))}
              </nav>
            </div>

            <div>
              <p className="mb-4 font-heading text-xs uppercase tracking-[0.2em] text-[rgb(var(--ala-on-primary))]/50">
                Account
              </p>
              <nav className="flex flex-col gap-3 font-body text-sm text-[rgb(var(--ala-on-primary))]/85">
                {ACCOUNT_LINKS.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    target={link.external ? '_blank' : undefined}
                    rel={link.external ? 'noopener noreferrer' : undefined}
                    className="transition hover:text-[rgb(var(--ala-accent))]"
                  >
                    {link.label}
                  </a>
                ))}
              </nav>
            </div>
          </div>
        </div>

        <div className="sr-only">
          <RichTextContent path="footer.company_logo_alt" label="Company Logo Alt Text" as="span" />
        </div>

        {/* Bottom: copyright */}
        <div className="border-t border-white/10 pt-6 text-center font-body text-xs text-[rgb(var(--ala-on-primary))]/55 [&_strong]:font-semibold [&_strong]:text-[rgb(var(--ala-on-primary))]/80">
          <RichTextContent path="footer.copyright" label="Copyright" as="span" />
          <RichTextContent
            path="footer.trademark_notice"
            label="Trademark Notice"
            as="p"
            className="mt-2"
          />
        </div>
      </div>
    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.company_logo': {
      label: 'Company Logo',
      type: 'image',
      defaultField: 'property_company_logo',
      defaultValue:
        '',
    },
    'footer.company_logo_alt': {
      label: 'Company Logo Alt Text',
      type: 'richtext',
      defaultField: 'property_company_name',
      defaultValue: '<p>American Landmark</p>',
    },
    'footer.headline': {
      label: 'Headline',
      type: 'richtext',
      defaultValue: '<p>Your Next Chapter Starts Here.</p>',
    },
    'footer.copyright': {
      label: 'Copyright',
      type: 'richtext',
      defaultValue:
        '<p>© Copyright 2026 <strong>American Landmark Apartments</strong> | Design &amp; Developed By <strong>Repli</strong> | Powered By <strong>MultiHub</strong></p>',
    },
    'footer.trademark_notice': {
      label: 'Trademark Notice',
      type: 'richtext',
      defaultValue:
        '<p>Landmark360® is a registered trademark or service mark of American Landmark Properties Management, LLC, or its affiliates.</p>',
    },
  },
  slotGroups: {
    'footer.contact_link': contactDef,
    'footer.social': socialDef,
  },
};

(Footer as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Footer;
